import { useRef, useState, useEffect } from "react";
import { Box, Grid, Dialog, DialogContent, DialogTitle, Typography, Paper, Button, DialogActions, IconButton } from "@mui/material";
import { DeleteIcon, ZoomIcon, UploadIcon, DragIcon } from "./icons/Icons";
import CloseIcon from '@mui/icons-material/Close';

import { DndContext, closestCenter, MeasuringStrategy, PointerSensor, useSensor, useSensors } from "@dnd-kit/core";
import { SortableContext, rectSortingStrategy, useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

function arrayMove(arr, from, to) {
    const updated = [...arr];
    const item = updated.splice(from, 1)[0];
    updated.splice(to, 0, item);
    return updated;
}

const MultiImageUploader = ({ images, onChange }) => {
    const [viewIdx, setViewIdx] = useState(null);

    const sensors = useSensors(useSensor(PointerSensor, {
        activationConstraint: { distance: 5 },
    }));

    const handleFiles = async (files) => {
        // accept FileList or array
        const fileList = Array.from(files || []);
        const imageFiles = fileList.filter(f => {
            // accept common image MIME types and common extensions as a fallback
            if (f.type && f.type.startsWith('image/')) return true;
            return /\.(jpe?g|png|gif)$/i.test(f.name);
        });

        if (!imageFiles.length) return;

        const toJpeg = (file) => new Promise((resolve, reject) => {
            const img = new Image();
            img.crossOrigin = 'anonymous';
            img.onload = () => {
                try {
                    const cw = 1280;
                    const ch = 960;
                    const canvas = document.createElement('canvas');
                    canvas.width = cw;
                    canvas.height = ch;
                    const ctx = canvas.getContext('2d');

                    // White background (JPEG has no transparency)
                    ctx.fillStyle = '#ffffff';
                    ctx.fillRect(0, 0, cw, ch);

                    // Cover: scale to fill 4:3, crop excess from center
                    const ratio = Math.max(cw / img.width, ch / img.height);
                    const nw = img.width * ratio;
                    const nh = img.height * ratio;
                    const dx = (cw - nw) / 2;
                    const dy = (ch - nh) / 2;

                    ctx.drawImage(img, dx, dy, nw, nh);

                    canvas.toBlob((blob) => {
                        if (!blob) return reject(new Error('Failed to convert image to blob'));
                        const nameBase = (file.name || 'image').replace(/\.[^/.]+$/, '');
                        const newFile = new File([blob], `${nameBase}.jpg`, { type: 'image/jpeg' });
                        try { URL.revokeObjectURL(img.src); } catch (e) { /* ignore */ }
                        resolve(newFile);
                    }, 'image/jpeg', 0.82);
                } catch (err) {
                    try { URL.revokeObjectURL(img.src); } catch (e) { /* ignore */ }
                    reject(err);
                }
            };
            img.onerror = (e) => {
                try { URL.revokeObjectURL(img.src); } catch (err) { /* ignore */ }
                reject(e);
            };

            // Use object URL for faster loading
            const url = URL.createObjectURL(file);
            img.src = url;
        });

        try {
            const processedFiles = await Promise.all(imageFiles.map(f => toJpeg(f)));
            const newImages = processedFiles.map(newFile => ({
                id: `${Date.now()}-${Math.random().toString(36).substring(2, 9)}`,
                file: newFile,
                url: URL.createObjectURL(newFile)
            }));
            onChange([...images, ...newImages]);
        } catch (err) {
            // keep silent but log for debugging
            // eslint-disable-next-line no-console
            console.error('Image processing error', err);
        }
    };

    const handleDelete = idx => {
        const updated = images.filter((_, i) => i !== idx);
        onChange(updated);
    };

    const handleDragEnd = (event) => {
        const { active, over } = event;
        if (!over || active.id === over.id) return;
        const oldIndex = images.findIndex(i => i.id === active.id);
        const newIndex = images.findIndex(i => i.id === over.id);
        if (oldIndex !== -1 && newIndex !== -1) {
            const next = arrayMove(images, oldIndex, newIndex);
            // Defer parent update to the next frame so it does not run in the same turn as
            // dnd-kit’s layout measurement / ResizeObserver work (reduces spurious RO warnings).
            requestAnimationFrame(() => onChange(next));
        }
    };

    const SortableImage = ({ img, idx }) => {
        const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: img.id });
        const style = {
            transform: CSS.Transform.toString(transform),
            transition,
            zIndex: isDragging ? 9999 : undefined,
        };

        return (
            <Grid item xs={2} key={img.id}>
                <Box
                    ref={setNodeRef}
                    sx={{ position: "relative" }}
                    style={style}
                    {...attributes}
                >
                    <Paper variant="outlined" sx={{ p: 1, height: 120, display: "flex", alignItems: "center", justifyContent: "center", overflow: "hidden" }}>
                        <img
                            src={img.url}
                            alt="preview"
                            style={{ maxWidth: "100%", maxHeight: "100%", cursor: "pointer" }}
                            onClick={() => setViewIdx(idx)}
                        />
                    </Paper>

                    <Box sx={{ position: 'absolute', top: 4, right: 4 }}>
                        <DeleteIcon size="small" onClick={() => handleDelete(idx)} title="Delete image" />
                    </Box>
                    <Box sx={{ position: 'absolute', bottom: 4, right: 4 }}>
                        <ZoomIcon size="small" onClick={() => setViewIdx(idx)} title="Preview image" />
                    </Box>
                    <Box sx={{ position: 'absolute', bottom: 4, left: 4 }}>
                        <DragIcon size="small" sx={{ cursor: 'grab' }} title="Drag to reorder" {...listeners} />
                    </Box>
                </Box>
            </Grid>
        );
    }

    // simple handlers to navigate preview
    const handlePrev = () => {
        if (viewIdx === null || images.length === 0) return;
        setViewIdx((viewIdx - 1 + images.length) % images.length);
    };

    const handleNext = () => {
        if (viewIdx === null || images.length === 0) return;
        setViewIdx((viewIdx + 1) % images.length);
    };

    return (
        <Box>
            <DropArea
                onFiles={handleFiles}
            />
            <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={handleDragEnd}
                measuring={{ droppable: { strategy: MeasuringStrategy.BeforeDragging } }}
            >
                <SortableContext items={images.map(i => i.id)} strategy={rectSortingStrategy}>
                    <Grid container spacing={2}>
                        {images.map((img, idx) => (
                            <SortableImage key={img.id} img={img} idx={idx} />
                        ))}
                    </Grid>
                </SortableContext>
            </DndContext>
            <PreviewDialog
                open={viewIdx !== null}
                images={images}
                index={viewIdx}
                onClose={() => setViewIdx(null)}
                onPrev={handlePrev}
                onNext={handleNext}
            />
        </Box>
    );
};

const DropArea = ({ onFiles }) => {
    const inputRef = useRef();

    const handleDrop = e => {
        e.preventDefault();
        if (e.dataTransfer.files) {
            onFiles(e.dataTransfer.files);
        }
    };

    return <Paper
        variant="outlined"
        sx={{ p: 2, mb: 2, borderStyle: "dashed", cursor: "pointer", display: 'flex', flexDirection: 'column', alignItems: 'center' }}
        onClick={() => inputRef.current.click()}
        onDrop={handleDrop}
        onDragOver={e => e.preventDefault()}
    >
        <UploadIcon childProps={{ fontSize: 'large' }} title="Upload images" />
        <Typography>Drag & drop images here, or click to select</Typography>
        <input
            ref={inputRef}
            type="file"
            accept="image/*"
            multiple
            style={{ display: "none" }}
            onChange={e => onFiles(e.target.files)}
        />
    </Paper>
}

const PreviewDialog = ({ open, images = [], index, onClose, onPrev, onNext }) => {
    useEffect(() => {
        if (!open) return;
        const onKey = (e) => {
            if (e.key === 'ArrowLeft') {
                e.preventDefault();
                onPrev && onPrev();
            } else if (e.key === 'ArrowRight') {
                e.preventDefault();
                onNext && onNext();
            } else if (e.key === 'Escape') {
                onClose && onClose();
            }
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [open, onPrev, onNext, onClose]);

    const image = (index !== null && index !== undefined) ? images[index] : null;

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md">
            <DialogTitle sx={{ m: 0, p: 2 }}>
                Image preview
                <IconButton aria-label="close" onClick={onClose} sx={{ position: 'absolute', right: 8, top: 8 }}>
                    <CloseIcon />
                </IconButton>
            </DialogTitle>
            <DialogContent sx={{ position: 'relative' }}>
                {image !== null && (
                    <Box sx={{ position: 'relative' }}>
                        <img
                            src={image.url}
                            alt="full"
                            style={{ width: "100%", maxHeight: "80vh", objectFit: "contain" }}
                        />

                        {/* bottom-left index badge */}
                        <Box sx={{ position: 'absolute', left: 8, bottom: 8, backgroundColor: 'rgba(0,0,0,0.6)', color: '#fff', px: 1, py: 0.5, borderRadius: 1 }}>
                            <Typography variant="body2">{`${(index !== null ? index + 1 : 0)}/${images.length}`}</Typography>
                        </Box>
                    </Box>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={onPrev} disabled={images.length === 0 || index === null}>
                    Previous
                </Button>
                <Button onClick={onNext} disabled={images.length === 0 || index === null}>
                    Next
                </Button>
            </DialogActions>
        </Dialog>
    );
}

export default MultiImageUploader;
